/**
 * Line Drag Configuration
 *
 * Type-specific configuration for line geometry dragging.
 * Defines how lines should be handled during whole-geometry drag operations.
 *
 * @module hooks/drag/configs/lineDragConfig
 */

import type { GeometryDragConfig } from '../useGeometryWholeDrag';
import { calculateLineCenter } from '../../../utils/geometry';
import { createLineGeoJson } from '../../../components/map/GeometryRenderer/LineRenderer/utils/lineGeoJsonHelpers';
import {
  LINE_SOURCE_PREFIX,
  LINE_HITBOX_PREFIX,
  POINT_HANDLE_SELECTOR,
} from '../../../constants/geometry';

/**
 * Creates configuration for line geometry drag behavior
 *
 * Line-specific behavior:
 * - Uses `linePoints` array for coordinates
 * - Calculates center dynamically using `calculateLineCenter()` (handles complex lines)
 * - Does not need to track original center (recalculates from points)
 * - Skips drag when clicking on point handles (vertex drag)
 *
 * @returns Configuration object for line drag
 */
export const createLineDragConfig = (): GeometryDragConfig => ({
  // MapLibre source prefix for lines
  sourcePrefix: LINE_SOURCE_PREFIX,

  // MapLibre layer prefix for line hitboxes (interactive layers)
  layerPrefix: LINE_HITBOX_PREFIX,

  // Skip drag when clicking on point handles (allows vertex drag)
  handleSelector: POINT_HANDLE_SELECTOR,

  // Extract line points from geometry
  getPoints: (geometry) => geometry.linePoints,

  // Create updated geometry with new line points
  createUpdatedGeometry: (_originalGeometry, newPoints) => ({
    linePoints: newPoints,
  }),

  // Calculate center from new line points
  // Recalculates dynamically (handles complex line shapes, branching, etc.)
  calculateCenter: (geometry, newPoints) => {
    return calculateLineCenter(newPoints) || geometry.calculatedCenter;
  },

  // Create GeoJSON for MapLibre preview during drag
  // Includes line interpolation for smooth curves
  createGeoJson: (geometry) => createLineGeoJson({ geometry }),

  // Line doesn't need original center (recalculates from points)
  needsOriginalCenter: false,
});
